import React, { useContext, createContext } from "react";
import useForm from "../../../Common/FormComponent/useForm";
import { convertToTime, getVideoDuration } from "../../../../utils/video";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import useMutation from "../../../../hooks/useMutation";
import CommunicationHubApis from "../../../../services/api/CommunicationHub";
import { setStatus } from "../../../../store/actions/User";
import { getRandomId } from "../../../../utils";

export const CreateElementContext = createContext(null);

// to parse local state to API format
const parseAssessment = ({ questions }) => {
  return questions.reduce(
    (a, b) => {
      a.questions.push(b.question);
      a.options.push({ option: b.choices });
      a.correct_options.push({ indices: b.answers?.map((item) => item - 1) });
      a.question_types.push(b.type);
      a.audio_urls.push(b.link ?? "");
      return a;
    },
    {
      questions: [],
      options: [],
      correct_options: [],
      question_types: [],
      audio_urls: [],
    }
  );
};
// to API format to local state
export const unParseAssessment = ({ initialData }) => {
  const questions = [];
  initialData.questions.forEach((item, index) => {
    const question = {
      question: item,
      type: initialData?.question_types?.[index],
      choices: initialData?.options?.[index]?.option,
      answers: (initialData?.correct_options?.[index]?.indices ?? []).map(
        (item) => item + 1
      ),
      link: initialData?.audio_urls?.[index] ?? "",
    };
    questions.push(question);
  });
  delete initialData?.question_types;
  delete initialData?.options;
  delete initialData?.correct_options;
  delete initialData?.audio_urls;
  return { ...initialData, questions };
};

const getInitialState = (elementType, initialData, edit) => {
  switch (elementType) {
    case "COMMUNICATION_TYPE_VIDEO":
      return {
        ...initialData,
        ...(!edit && {
          content: [
            {
              lang_code: "en",
              title: "",
              description: "",
            },
          ],
        }),
      };
    case "ASSESSMENT":
      initialData.content = initialData.content.map((item) =>
        unParseAssessment({ initialData: item })
      );
      return {
        ...initialData,
        randomize_questions: {
          randomize_questions: !!initialData?.randomize_questions_count,
        },
        randomize_questions_count: initialData?.randomize_questions_count ?? 0,
      };
    case "SERIES":
      return {
        ...initialData,
        sequence_details: (initialData.sequence_details ?? [{}]).map(
          (item) => ({ ...item, id: getRandomId() })
        ),
        buffer_hours: initialData.buffer_hours ?? 48,
        priority: initialData.priority ?? 1,
      };
  }
};

const CreateElementContextProvider = ({
  children,
  initialData,
  edit,
  elementType: element_type,
  options,
}) => {
  const form = useForm({
    ...getInitialState(element_type, initialData, edit),
    element_type,
    request_type: edit ? "UPDATE" : "CREATE",
  });

  const history = useHistory();
  const { pathname } = useLocation();

  const { mutate: createMutation } = useMutation(
    CommunicationHubApis.createElement
  );
  const { mutate: updateMutation } = useMutation(
    CommunicationHubApis.updateElement
  );

  const dispatch = useDispatch();

  const handleCreate = async () => {
    try {
      // checking errors,
      if (Object.values(form.errors).some((item) => !!item)) {
        dispatch(
          setStatus({
            message: "Please correct all the error(s)",
            type: "error",
          })
        );
      } else {
        const payload = {
          ...form.values,
          ...(element_type === "COMMUNICATION_TYPE_VIDEO" && {
            pass_percentage: parseInt(form.values.pass_percentage),
            buffer_hours: parseInt(form.values?.buffer_hours),
            tags: (form?.values?.tags ?? []).map((tag) => tag.value),
          }),
          ...(element_type === "ASSESSMENT" && {
            content: form.values.content.map((item) => ({
              ...item,
              ...parseAssessment({ questions: item.questions }),
            })),
            pass_percentage: parseInt(form.values.pass_percentage),
            buffer_hours: parseInt(form.values?.buffer_hours),
            randomize_questions_count: form.values?.randomize_questions
              ?.randomize_questions
              ? parseInt(form.values.randomize_questions_count ?? "0")
              : 0,
            randomize_questions: undefined,
          }),
          ...(element_type === "SERIES" && {
            sequence_details: form.values.sequence_details.map((item) => ({
              ref_id: item.ref_id,
              ref_type: item.ref_type,
            })),
            buffer_hours: parseInt(form.values?.buffer_hours),
          }),
        };
        if (edit) {
          await updateMutation(payload);
          history.replace(pathname);
        } else {
          await createMutation(payload);
          history.push("/communication-hub");
        }
        dispatch(
          setStatus({
            message: "Successfully updated",
            type: "success",
          })
        );
      }
    } catch (err) {
      console.log({ err });
      dispatch(
        setStatus({
          message: "Something went wrong",
          type: "error",
        })
      );
    }
  };

  const handleDelete = (index) => {
    form.setFields((prev) => ({
      ...prev,
      content: prev.content.filter((item, i) => i !== index),
    }));
  };

  const handleAdd = (langCode) => {
    form.setFields((prev) => ({
      ...prev,
      content: [
        ...prev.content,
        {
          lang_code: langCode,
          title: "",
          description: "",
          ...(element_type === "COMMUNICATION_TYPE_VIDEO"
            ? { url: "", duration: "" }
            : element_type === "ASSESSMENT"
            ? {
                questions: [
                  {
                    question: "",
                    type: "SINGLE_CHOICE",
                    choices: [],
                    answers: [],
                  },
                ],
              }
            : {}),
        },
      ],
    }));
  };

  const makeDefaultLanguage = (index) => {
    form.setFields((prev) => ({
      ...prev,
      content: [
        prev.content[index],
        ...prev.content.filter((item, i) => i !== index),
      ],
    }));
  };

  const handleImage =
    (key) =>
    ({ file, url }) => {
      form.setFields((prev) => ({
        thumbnail: {
          ...prev.thumbnail,
          [key]: url,
        },
      }));
    };

  const handleVideoImage =
    (index) =>
    async ({ file, url }) => {
      const duration = await getVideoDuration(file);
      form.setFields((prev) => {
        prev.content[index].url = url;
        prev.content[index].duration = convertToTime(duration);
        return { content: [...prev.content] };
      });
    };
  const value = {
    form,
    edit,
    element_type,
    options,
    handleImage,
    handleVideoImage,
    handleCreate,
    handleAdd,
    handleDelete,
    makeDefaultLanguage,
  };
  return (
    <CreateElementContext.Provider value={value}>
      {children}
    </CreateElementContext.Provider>
  );
};

export const useCreateElementContext = () => {
  const context = useContext(CreateElementContext);
  if (!context) {
    throw Error(
      `${CreateElementContext.displayName} not found!!!, you might have forget wrapping the provider or maybe accessing it at the wrong place.`
    );
  }
  return context;
};

CreateElementContext.displayName = "CreateElementContext";
export default CreateElementContextProvider;
